import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling, ServiceError } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import type { Database } from '@/integrations/supabase/types';

type SocialAccount = Database['public']['Tables']['social_accounts']['Row'];
type SocialAccountUpdate = Database['public']['Tables']['social_accounts']['Update'];

export class SocialAccountsService {
  private static readonly TABLE = 'social_accounts';
  private static readonly logger = logger.child({ service: 'SocialAccountsService' });

  /**
   * Get all social accounts for a user
   */
  static async getByUser(userId: string): Promise<SocialAccount[]> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Fetching user social accounts', { userId });

      const { data, error } = await supabase
        .from(this.TABLE)
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('platform', { ascending: true });

      if (error) throw error;

      this.logger.info('Social accounts fetched', { 
        userId, 
        count: data?.length || 0 
      });

      return data || [];
    }, { operation: 'getByUser', table: this.TABLE });
  }

  /**
   * Get a single social account by ID
   */
  static async getById(id: string): Promise<SocialAccount | null> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Fetching social account', { accountId: id });

      const { data, error } = await supabase
        .from(this.TABLE)
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Social account fetched', { accountId: id, found: !!data });

      return data;
    }, { operation: 'getById', table: this.TABLE });
  }

  /**
   * Deactivate a social account
   */
  static async deactivate(id: string): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Deactivating social account', { accountId: id });

      const { error } = await supabase
        .from(this.TABLE)
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      this.logger.info('Social account deactivated', { accountId: id });
    }, { operation: 'deactivate', table: this.TABLE });
  }

  /**
   * Activate a social account
   */
  static async activate(id: string): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Activating social account', { accountId: id });

      const { error } = await supabase
        .from(this.TABLE)
        .update({ is_active: true })
        .eq('id', id);

      if (error) throw error;

      this.logger.info('Social account activated', { accountId: id });
    }, { operation: 'activate', table: this.TABLE });
  }

  /**
   * Update OAuth tokens for a social account
   */
  static async updateTokens(
    id: string,
    tokens: { access_token?: string; refresh_token?: string }
  ): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Updating social account tokens', { accountId: id });

      const updates: SocialAccountUpdate = {};
      if (tokens.access_token) updates.access_token = tokens.access_token;
      if (tokens.refresh_token) updates.refresh_token = tokens.refresh_token;

      const { error } = await supabase
        .from(this.TABLE)
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      this.logger.info('Social account tokens updated', { accountId: id });
    }, { operation: 'updateTokens', table: this.TABLE });
  }

  /**
   * Get health status summary for user's accounts
   */
  static async getHealthStatus(userId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Fetching account health status', { userId });

      const { data, error } = await supabase
        .from(this.TABLE)
        .select('id, platform, account_name, health_status, health_check_error, token_expires_at, last_health_check')
        .eq('user_id', userId)
        .eq('is_active', true);

      if (error) throw error;

      const accounts = data || [];
      const summary = {
        total: accounts.length,
        healthy: accounts.filter((a) => a.health_status === 'healthy').length,
        warning: accounts.filter((a) => a.health_status === 'warning').length,
        expired: accounts.filter((a) => a.health_status === 'expired').length,
        error: accounts.filter((a) => a.health_status === 'error').length,
        accounts,
      };

      this.logger.info('Health status fetched', { userId, summary });

      return summary;
    }, { operation: 'getHealthStatus', table: this.TABLE });
  }

  /**
   * Trigger manual health check for user's accounts
   */
  static async checkHealth(userId: string): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Triggering health check', { userId });

      const { data, error } = await supabase.functions.invoke('check-connection-health', {
        body: { userId },
      });

      if (error) throw error;

      this.logger.info('Health check completed', { userId, result: data });
    }, { operation: 'checkHealth', table: this.TABLE });
  }
}
